<template>
  <v-card flat id="datasetStatsCard">
    <v-toolbar flat color="grey lighten-3">
      <v-toolbar-title>{{ $t('app.label.dataset') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon disabled>
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-toolbar>
    <div id="project_dataset_info">
      <v-container v-if="stats" id="datasetStats">
        <v-layout align-space-around justify-space-around fill-height>
          <v-flex xs12 md3>
            <v-card v-if="isSequenceLabellingProject" flat>
              <v-card-text id="datasetStatsDocs" class="text--primary">
                <strong>{{ stats.annotation.documentsInDataset }}</strong>
                {{ $tc('dataset.common.documents', stats.annotation.documentsInDataset) }}
                (<strong>{{ stats.annotation.documentsInDataset / stats.corpus.documentCount | numeral('0.[0]%') }}</strong>)
              </v-card-text>
              <v-card-text id="datasetStatsSegs" class="text--primary">
                <strong>{{ stats.annotation.segmentsInDataset }}</strong>
                {{ $tc('dataset.common.segments', stats.annotation.segmentsInDataset) }}
                (<strong>{{ stats.annotation.segmentsInDataset / stats.corpus.segmentCount | numeral('0.[0]%') }}</strong>)
              </v-card-text>
              <v-card-text id="datasetStatsSegs" class="text--primary">
                <strong>{{ stats.annotation.annotationCount }}</strong>
                {{ $tc('dataset.common.annotations', stats.annotation.annotationCount) }}
              </v-card-text>
            </v-card>
            <v-card v-if="isClassificationProject" flat>
              <v-card-text id="datasetClassificationStatsDocs" class="text--primary">
                <strong>{{ stats.categories.documentsInDataset }}</strong>
                {{ $tc('dataset.common.documents', stats.categories.documentsInDataset) }}
                (<strong>{{ stats.categories.documentsInDataset / stats.corpus.documentCount | numeral('0.[0]%') }}</strong>)
              </v-card-text>
              <v-card-text id="datasetStatsSegs" class="text--primary">
                <strong>{{ stats.categories.categoriesCount }}</strong>
                {{ $tc('dataset.common.categories', stats.categories.categoriesCount) }}
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex v-if="isSequenceLabellingProject" id="datasetStatsAnnots" md6>
            <apexchart
                ref="donut"
                width="100%"
                height="320"
                type="donut"
                :options="annotationDonut.chartOptions"
                :series="annotationDonut.series"
                class="hidden-xs-only"
            ></apexchart>
          </v-flex>
          <v-flex v-if="isClassificationProject" id="datasetStatsCategories" md6>
            <apexchart
                ref="donut"
                width="100%"
                height="320"
                type="donut"
                :options="categorieDonut.chartOptions"
                :series="categorieDonut.series"
                class="hidden-xs-only"
            ></apexchart>
          </v-flex>
          <v-flex v-if="metafacets && metafacets.length > 0 && stats.annotation.segmentsInDataset > 2" md4>
            <v-select
                v-show="metafacets.length > 1"
                id="datasetMetadataSelectorSb"
                v-model="metadataSelector"
                :items="metafacets"
                outlined
                :label="$t('dataset.common.metadataSelection')"
                item-text="text"
                item-value="value"
                @change="fetchStats()"
            ></v-select>
            <apexchart
                ref="datasetDonut"
                width="100%"
                height="350"
                type="radar"
                :options="datasetDonut.chartOptions"
                :series="datasetDonut.series"
                class="hidden-xs-only"
            ></apexchart>
          </v-flex>
          <v-flex v-else md4></v-flex>
        </v-layout>
      </v-container>
      <v-container v-else>
        <v-progress-circular :value="0" id="emptyDatasetData"/>
      </v-container>
    </div>
  </v-card>
</template>

<script>
import {mapState, mapGetters, mapActions} from "vuex";

export default {
  data() {
    return {
      metadataSelector: ""
    };
  },
  mounted: function () {
    if (this.project.metafacets && this.project.metafacets.length > 0) {
      this.metadataSelector = this.project.metafacets[0]
    } else {
      this.metadataSelector = ""
    }
    this.getDatasetStats(this.corpusMetadataSelector);
  },
  computed: {
    ...mapState(["project"]),
    ...mapGetters(["isSequenceLabellingProject", "isClassificationProject"]),
    ...mapState("dataset", ["stats"]),
    ...mapState("settings", ["labelsByName"]),
    metafacets() {
      return this.project.metafacets
    },
    datasetDonut() {
      var series = [{name: "value", data: []}];
      var labels = [];
      if (
          this.stats.annotation.documentFacets &&
          this.stats.annotation.documentFacets.facets
      ) {
        let facetsCnt = 0;
        // eslint-disable-next-line
        let otherCnt = 0;
        this.stats.annotation.documentFacets.facets.forEach(element => {
          if (facetsCnt < 12 && element.segmentCount > 1) {
            let docFacet = this.stats.corpus.documentFacets.facets.find(
                f => f._id === element._id
            );
            labels.push(
                !element._id
                    ? this.$t("dataset.common.no_value")
                    : element._id
            );
            series[0].data.push(
                (100 *
                    (element.segmentCount / this.stats.annotation.segmentsInDataset)) /
                ((docFacet ? docFacet.segmentCount : 0) /
                    this.stats.corpus.segmentCount)
            );
            facetsCnt++;
          } else {
            // eslint-disable-next-line
            otherCnt += element.segmentCount;
          }
        });
      }
      return {
        series,
        chartOptions: {
          yaxis: {
            show: false,
            forceNiceScale: true,
            decimalsInFloat: 0,
            labels: {
              formatter: function (value) {
                return value.toFixed(0) + "%";
              }
            }
          },
          chart: {
            events: {
              dataPointSelection: function (
                  event,
                  chartContext,
                  {w, dataPointIndex}
              ) {
                console.log(`clicked on ${w.config.labels[dataPointIndex]}`);
              }
            }
          },
          title: {
            text: this.$t("dataset.annotation.datasetDistribution", {
              metadata: this.stats.annotation.documentFacets.metadata
            }),
            align: "center",
            margin: 10,
            style: {
              fontSize: "15px"
            }
          },
          legend: {
            horizontalAlign: "center",
            offsetY: 50
          },
          labels
        }
      };
    },
    categorieDonut() {
      // eslint-disable-next-line
      let statsCatego = this.stats.categories;
      var series = [];
      var labels = [];
      var labelIds = [];
      var colors = [];
      if (
          this.stats.categories &&
          this.stats.categories.categoriesFacets &&
          this.stats.categories.categoriesFacets.labels
      ) {
        let otherCnt = 0;
        const categoriesCount = this.stats.categories.categoriesCount;
        this.stats.categories.categoriesFacets.labels.forEach(element => {
          // diplay labels if they are frequent enough (1.5% of total):
          // it looks like the graph library is able to display 1.5% rather nicely...
          // but narrower pie slices are hardly visible (and  interesting)
          // (100 / 1.5 = 67)
          // this algorithm is still missing the point of displaying many 100 evenly distributed labels
          if (element.count > categoriesCount / 67) {
            let actualLabel = this.labelsByName[element._id];
            if (actualLabel) {
              labelIds.push(actualLabel.name);
              labels.push(actualLabel.label);
              series.push(element.count);
              colors.push(actualLabel.color);
            }
          } else {
            otherCnt += element.count;
          }
        });
        if (otherCnt > 0) {
          labels.push(this.$t("dataset.common.other"));
          series.push(otherCnt);
          colors.push("grey");
        }
      }
      return {
        series,
        chartOptions: {
          chart: {
            toolbar: {
              show: true,
              offsetX: 0,
              offsetY: 0,
              tools: {
                download: true,
                selection: true,
              },
              export: {
                csv: {
                  filename: undefined,
                  columnDelimiter: '\t',
                  headerCategory: 'label',
                  headerValue: 'count',
                  dateFormatter(timestamp) {
                    return new Date(timestamp).toDateString()
                  }
                },
                svg: {
                  filename: undefined,
                },
                png: {
                  filename: undefined,
                }
              },
              autoSelected: 'zoom'
            },
            events: {
              dataPointSelection: function (
                  event,
                  chartContext,
                  {w, dataPointIndex}
              ) {
                console.log(`clicked on ${w.config.labelIds[dataPointIndex]}`);
              }
            }
          },
          yaxis: {
            show: true,
            forceNiceScale: true,
            decimalsInFloat: 0
          },
          dataLabels: {
            enabled: true,
            // eslint-disable-next-line
            formatter: function (value, {seriesIndex, w}) {
              //return `${w.config.labels[seriesIndex]} (${Math.floor(value)}%)`;
              return `${Math.floor(value)}%`;
            }
          },
          title: {
            text: this.$t("dataset.common.categoriesDistribution", {
              categoriesCount: this.stats.categories ? this.stats.categories.categoriesCount : 0
            }),
            align: "center",
            margin: 10,
            style: {
              fontSize: "15px"
            }
          },
          colors,
          labels,
          labelIds,
          plotOptions: {
            pie: {
              donut: {
                size: "35%"
              }
            }
          },
          legend: {
            horizontalAlign: "center",
            offsetY: 50
          }
        }
      };
    },
    annotationDonut() {
      // eslint-disable-next-line
      let statsAnnot = this.stats.annotation;
      var series = [];
      var labels = [];
      var labelIds = [];
      var colors = [];
      if (
          this.stats.annotation.annotationFacets &&
          this.stats.annotation.annotationFacets.labels
      ) {
        let otherCnt = 0;
        const annotationCount = this.stats.annotation.annotationCount;
        this.stats.annotation.annotationFacets.labels.forEach(element => {
          // diplay labels if they are frequent enough (1.5% of total):
          // it looks like the graph library is able to display 1.5% rather nicely...
          // but narrower pie slices are hardly visible (and  interesting)
          // (100 / 1.5 = 67)
          // this algorithm is still missing the point of displaying many 100 evenly distributed labels
          if (element.count > annotationCount / 67) {
            let actualLabel = this.labelsByName[element._id];
            if (actualLabel) {
              labelIds.push(actualLabel.name);
              labels.push(actualLabel.label);
              series.push(element.count);
              colors.push(actualLabel.color);
            }
          } else {
            otherCnt += element.count;
          }
        });
        if (otherCnt > 0) {
          labels.push(this.$t("dataset.common.other"));
          series.push(otherCnt);
          colors.push("grey");
        }
      }
      return {
        series,
        chartOptions: {
          chart: {
            toolbar: {
              show: true,
              offsetX: 0,
              offsetY: 0,
              tools: {
                download: true,
                selection: true,
              },
              export: {
                csv: {
                  filename: undefined,
                  columnDelimiter: '\t',
                  headerCategory: 'label',
                  headerValue: 'count',
                  dateFormatter(timestamp) {
                    return new Date(timestamp).toDateString()
                  }
                },
                svg: {
                  filename: undefined,
                },
                png: {
                  filename: undefined,
                }
              },
              autoSelected: 'zoom'
            },
            events: {
              dataPointSelection: function (
                  event,
                  chartContext,
                  {w, dataPointIndex}
              ) {
                console.log(`clicked on ${w.config.labelIds[dataPointIndex]}`);
              }
            }
          },
          yaxis: {
            show: true,
            forceNiceScale: true,
            decimalsInFloat: 0
          },
          dataLabels: {
            enabled: true,
            // eslint-disable-next-line
            formatter: function (value, {seriesIndex, w}) {
              //return `${w.config.labels[seriesIndex]} (${Math.floor(value)}%)`;
              return `${Math.floor(value)}%`;
            }
          },
          title: {
            text: this.$t("dataset.common.annotDistribution", {
              annotationCount: this.stats.annotation.annotationCount
            }),
            align: "center",
            margin: 10,
            style: {
              fontSize: "15px"
            }
          },
          colors,
          labels,
          labelIds,
          plotOptions: {
            pie: {
              donut: {
                size: "35%"
              }
            }
          },
          legend: {
            horizontalAlign: "center",
            offsetY: 50
          }
        }
      };
    },
  },
  methods: {
    ...mapActions("dataset", ["getDatasetStats", "getCorpusStats", "downloadDataset"]),
    fetchStats() {
      this.getDatasetStats(this.metadataSelector);
      this.getCorpusStats(this.metadataSelector);
    },
  }
};
</script>
